import { getCountries, type CountryCode } from "libphonenumber-js/min";
import { detectBrowserTimezone } from "@/components/ui/timezone-select";
import timezoneCountries from "@/lib/geo/timezone-countries.json";

export const DEFAULT_COUNTRY_CODE: CountryCode = "US";

const VALID_COUNTRIES = new Set(getCountries());

function parseCountryCode(code: string): CountryCode | undefined {
  const upper = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return undefined;
  return VALID_COUNTRIES.has(upper as CountryCode)
    ? (upper as CountryCode)
    : undefined;
}

export function countryFromTimezone(
  timezone: string,
): CountryCode | undefined {
  const countries =
    timezoneCountries[timezone as keyof typeof timezoneCountries];
  if (!countries) return undefined;

  for (const code of countries.split(",")) {
    const parsed = parseCountryCode(code);
    if (parsed) return parsed;
  }

  return undefined;
}

export function resolveCountryCode(
  timezone = detectBrowserTimezone(),
): CountryCode {
  return countryFromTimezone(timezone) ?? DEFAULT_COUNTRY_CODE;
}

export async function detectCountryCode(
  timezone = detectBrowserTimezone(),
): Promise<CountryCode> {
  try {
    const response = await fetch("/api/geo/country");
    if (response.ok) {
      const data = (await response.json()) as { country?: string | null };
      const fromIp = data.country ? parseCountryCode(data.country) : undefined;
      if (fromIp) return fromIp;
    }
  } catch {
    // Fall back to timezone mapping below.
  }

  return resolveCountryCode(timezone);
}
